import * as React from 'react';
import { observer } from 'mobx-react';
import {
    BasicCollapse,
    BasicTable,
    Button,
    IBasicTableColumn,
    Icon,
    IconButton,
    IconTextButton,
    IStepItem,
    Label,
    FieldWithLabel,
    notification,
    Progress,
    RowLayout,
    Skeleton,
    Space,
    TableListingPage,
    Typography,
    useModal,
    VerticalStepper,
} from 'ui-lib';
import cx from 'classnames';
import {
    useAccountStore,
    useAuthStore,
    useDeploymentGroupsStore,
    useDeploymentStore,
    useDeviceGroupsStore,
    useStores,
    useSnapshotStore,
} from '../../../../store';
import { TDisplayDeploymentProps } from '../../../../store/deployment-store';
import { useDeploymentStates } from './common';
import { confirmAction } from '../../admin/utils';
import { hasPermission } from '../../utils';
import { getTimezones, SOFTWARE_MANAGEMENT_ROUTES } from '../index';
import { SearchableSelectComponent } from '../../components/searchable-component';

import { NOTIFICATION_TYPE } from '../../../../dto/notifications-dto';

import { DeviceUuidHyperlink } from 'pages/private/components/device-uuid-hyperlink-component';
import { PROGRESS_STAGE, UnifiedDeploymentUnitType } from 'dto/deployment/deployment-dto';
import {
    EXPORT_TYPES,
    exportData,
    getErrorMessage,
    getMovingToDeviceGroupMessage,
    isNullOrEmpty,
    MAX_TIMES_TO_RE_FETCH_DATA,
    TIME_BEFORE_RE_FETCH_IN_SECONDS,
} from 'common-utils';
import { dateCompare, formatDate } from 'common-utils/date-utils';
import { useLocation } from 'react-router-dom';

import deploymentCss from './deployments.css';
import { showPackagesWithDescriptionModal } from '../../components/package-modal-components';
import { AccountType } from 'dto/access-management/account-dto';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { useManagedDeploymentGroups } from 'hooks/deployment-groups/use-manage-deployment-groups.hook';
import { useDeploymentPageNavigation } from './hooks/use-deployment-page-navigation';
import { DeploymentDetails, DeploymentType } from 'dto/deployment/deployment-details.dto';
import { RaptorComponentHelpers } from '../raptor/components/helpers';
import { useLoadingStates } from 'hooks/use-loading.hook';
import {
    DeploymentListLocationState,
    ICancelExecutionSchedulePayload,
    IDeploymentDetailsDeviceGroupRecord,
    IDeploymentDetailsDeviceRecord,
    IDeploymentUnitCollapseProps,
    IDeploymentUnitDisplayItem,
    IDeviceGroupDetailsCollapseProps as IDeploymentDetailsCollapse,
} from './list.types';
import { DeploymentListingHelpers } from './list.helpers';
import { TableUtils } from 'pages/private/components/table-utils';

export const DeploymentsManagementTable = observer(() => {
    const location = useLocation();
    const locationState = location.state as DeploymentListLocationState;
    const stores = useStores();
    const { navigateToDeploymentCreate } = useDeploymentPageNavigation();
    const accountStore = useAccountStore();
    const { assureApiAccount, currentUser } = useAuthStore();
    const deploymentGroupStore = useDeploymentGroupsStore();
    const deploymentStore = useDeploymentStore();

    const { onGroupChange, loadDeployments, deployments, onFilter, selectedGroupId } = useDeploymentStates();
    const currentAccount = React.useMemo(() => accountStore.getAccountById(currentUser.accountId), [currentUser]);

    const { displayDeploymentGroups, selectedAccount, selectAccountSource, setSelectedAccount } =
        useManagedDeploymentGroups({
            accountId: locationState?.clientAccountId ?? currentUser.accountId,
        });

    const hasModifyPermission = hasPermission(SOFTWARE_MANAGEMENT_ROUTES.DEPLOYMENTS_MODIFY, stores);
    const deploymentGroupSelectOptions = React.useMemo(
        () =>
            displayDeploymentGroups.map((group) => ({
                ...group,
                label: group.displayLabel,
            })),
        [displayDeploymentGroups],
    );

    const [downloading, setDownloading] = React.useState(false);

    React.useEffect(() => {
        const groupId = selectedGroupId ?? locationState?.deploymentGroupId;
        if (isNullOrEmpty(displayDeploymentGroups) || !displayDeploymentGroups.find((item) => item.id === groupId)) {
            onGroupChange(undefined);
            return;
        }
        onGroupChange(groupId);
    }, [displayDeploymentGroups]);

    const onDelete = async (id: number) => {
        try {
            await deploymentStore.deleteDeploymentById(assureApiAccount.accountId, id);
            notification.success({
                message: 'Selected deployment has been deleted',
                placement: 'topRight',
            });
            await stores.notificationsStore.create({
                content: {
                    title: 'Deployments',
                    text: 'Selected deployment has been deleted',
                    messageType: 'Deployment',
                },
                type: NOTIFICATION_TYPE.PERSONAL,
                receiverUserUuid: currentUser.uuid,
                sender: currentUser.userName,
            });
        } catch (err) {
            notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const onCancelExecutionSchedule = async (payload: ICancelExecutionSchedulePayload) => {
        try {
            await deploymentStore.cancelExecutionSchedule(assureApiAccount.accountId, payload.deploymentId);
            notification['success']({
                message: 'Selected deployment execution has been cancelled',
                placement: 'topRight',
            });
            await stores.notificationsStore.create({
                content: {
                    title: 'Deployments',
                    text: 'Selected deployment execution has been cancelled',
                    messageType: 'Deployment',
                },
                type: NOTIFICATION_TYPE.PERSONAL,
                receiverUserUuid: currentUser.uuid,
                sender: currentUser.userName,
            });
        } catch (err) {
            return notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const onExportDeploymentProcess = async (record: TDisplayDeploymentProps): Promise<void> => {
        try {
            setDownloading(true);
            const deploymentGroup = displayDeploymentGroups.find((group) => group.id === record.deploymentGroupId);
            const deploymentDetails = await deploymentStore.getDeploymentDetails(
                accountStore.getAccountByUuid(record.accountUuid).id,
                record.id,
                { includeDeviceConnectivity: true },
            );
            exportData(
                `${record.label}.csv`,
                EXPORT_TYPES.CSV,
                DeploymentListingHelpers.getExportRows(deploymentDetails, deploymentGroup?.label),
                {
                    uid: 'UID',
                    serialNumber: 'Serial number',
                    esn: 'eSN',
                    firmwareVersion: 'Firmware version',
                    softwareUpdateStatus: 'Status',
                    deviceGroup: 'Device group',
                    deploymentGroup: 'Deployment group',
                    deploymentName: 'Deployment name',
                    deploymentUnits: deploymentDetails.type === DeploymentType.ASSURE ? 'Release' : 'Component Versions'
                },
            );
        } catch (err) {
            return notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        } finally {
            setDownloading(false);
        }
    };

    const hasPermissionToCancel = (accountUuid: string): boolean => {
        const account = accountStore.getAccountByUuidOrParentUuidForClientAccount(accountUuid);
        return accountUuid === currentAccount.uuid || account?.parent?.uuid === currentAccount.uuid;
    };

    const columns: IBasicTableColumn[] = [
        TableUtils.createBasicFilterColumn({ key: 'label', title: 'Deployment name' }),
        TableUtils.createBasicFilterColumn({ key: 'status', title: 'Status' }),
        TableUtils.createBasicFilterTimeColumn({
            key: 'createdAtFormatted',
            title: 'Created date',
            compareKey: 'createdAtTimestamp',
        }),
        TableUtils.createBasicFilterTimeColumn({
            key: 'scheduleDateTimeFormatted',
            title: 'Scheduled date',
            compareKey: 'scheduleDateTimestamp',
        }),
        {
            code: 'action',
            title: 'Action',
            dataIndex: 'action',
            render: (_, record: TDisplayDeploymentProps): JSX.Element => {
                return !hasModifyPermission ? null : (
                    <Space>
                        {['SCHEDULED', 'IN_PROGRESS'].includes(record.status) ? (
                            <Button
                                type='link'
                                htmlType='button'
                                disabled={!hasPermissionToCancel(record.accountUuid)}
                                onClick={(e) =>
                                    confirmAction(
                                        { status: record.status, deploymentId: record.id },
                                        `${e.target['outerText']}`,
                                        `Do you want to ${e.target['outerText']} this deployment task?`,
                                        onCancelExecutionSchedule,
                                    )
                                }
                            >
                                Cancel
                            </Button>
                        ) : null}
                        {record.status === 'IN_PROGRESS' ? (
                            <IconTextButton
                                key='exportInProgressDeployments'
                                label='Export to CSV'
                                type='primary'
                                onClick={(): Promise<void> => onExportDeploymentProcess(record)}
                                loading={downloading}
                            />
                        ) : null}
                        {['SCHEDULED', 'IN_PROGRESS', 'CANCELLED'].includes(record.status) ? null : (
                            <Button
                                type='link'
                                htmlType='button'
                                disabled={record.accountUuid !== currentAccount.uuid}
                                danger
                                onClick={() =>
                                    confirmAction(
                                        record.id,
                                        'Delete deployment',
                                        'Do you want to delete this deployment task?',
                                        onDelete,
                                    )
                                }
                            >
                                Delete
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div className={deploymentCss.tabChild}>
            <Skeleton loading={deploymentGroupStore.dataLoading} noOfRows={2}>
                <FieldWithLabel
                    label='Account'
                    hidden={currentUser.accountType == AccountType.CLIENT}
                    align='right'
                    children={
                        <OrganisationSelect
                            placeholder='Select Account'
                            style={{ width: 'unset' }}
                            treeDataSource={selectAccountSource}
                            expandAll={true}
                            onChange={(value): void => {
                                setSelectedAccount(accountStore.getAccountById(value));
                            }}
                            value={selectedAccount?.id}
                            disabled={deploymentGroupStore.dataLoading}
                        />
                    }
                />
                <FieldWithLabel
                    label='Deployment group'
                    align='right'
                    children={
                        <SearchableSelectComponent
                            code='deploymentGroupsItem'
                            labelInValue={true}
                            placeholder='Select a deployment group'
                            dataSource={deploymentGroupSelectOptions}
                            onChange={(item) => {
                                onGroupChange(item.value);
                            }}
                            value={selectedGroupId}
                        />
                    }
                />
            </Skeleton>

            <TableListingPage
                title={''}
                tableProps={{
                    title: 'Deployments',
                    loadingState: deploymentStore.dataLoading,
                    table: {
                        columns: columns,
                        dataSource: deployments,
                        expandable: {
                            expandedRowClassName: () => deploymentCss.deploymentDetailCollapse,
                            expandedRowRender: (row: TDisplayDeploymentProps) => (
                                <DeploymentDetailsCollapse selectedDeployment={row} />
                            ),
                            expandIcon: ({ expanded, onExpand, record }) =>
                                expanded ? (
                                    <Icon iconName={'angle-down'} size={'LG'} onClick={(e) => onExpand(record, e)} />
                                ) : (
                                    <Icon iconName={'angle-right'} size={'LG'} onClick={(e) => onExpand(record, e)} />
                                ),
                            defaultExpandedRowKeys: locationState?.deploymentId ? [locationState.deploymentId] : [],
                        },
                        extraOptions: {
                            pagination: { hideOnSinglePage: true },
                        },
                    },
                    toolbar: {
                        onSearch: onFilter,
                        searchPlaceholder: 'Deployment name',
                        onReload: () => {
                            loadDeployments(true);
                        },
                        onAdd: hasModifyPermission ? navigateToDeploymentCreate : null,
                    },
                }}
            />
        </div>
    );
});

const DeploymentUnitCollapse = (props: IDeploymentUnitCollapseProps): JSX.Element => {
    const { accountId, units } = props;
    const snapshotStore = useSnapshotStore();
    const [modal, contextHolder] = useModal();
    const loadingHook = useLoadingStates();
    const [displayedItems, setDisplayedItems] = React.useState<IDeploymentUnitDisplayItem[]>([]);
    const collapsePanelTitle = React.useMemo(() => {
        if (isNullOrEmpty(units)) {
            return 'Release';
        }
        switch (units[0].type) {
            case UnifiedDeploymentUnitType.COMPONENT_VERSION:
                return 'Component Versions';
            default:
                return 'Release';
        }
    }, [units]);

    React.useEffect(() => {
        loadingHook.withLoading(async () => {
            const unitDetails = units.map((unit) => {
                if (unit.type === UnifiedDeploymentUnitType.COMPONENT_VERSION) {
                    return {
                        uuid: unit.uuid,
                        label: unit.displayName,
                        onView: () =>
                            RaptorComponentHelpers.showComponentVersionDetailsModal(modal, {
                                accountId,
                                componentVersionUuid: unit.uuid,
                            }),
                    };
                }
                const release = snapshotStore.getSnapshot(unit.id);
                return {
                    uuid: unit.uuid,
                    label: unit.displayName,
                    onView: () =>
                        showPackagesWithDescriptionModal(
                            snapshotStore.getPackagesBySnapshotId(release.id, true),
                            release.description,
                        ),
                };
            });
            setDisplayedItems(unitDetails);
        });
    }, [units]);

    return (
        <React.Fragment>
            {contextHolder}
            <BasicCollapse
                defaultActiveKey={['deploymentUnitsPanelHeader']}
                panels={[
                    {
                        header: <span className={deploymentCss.collapseHeader}>{collapsePanelTitle}</span>,
                        key: 'deploymentUnitsPanelHeader',
                        content: (
                            <Skeleton loading={loadingHook.loading} noOfRows={1}>
                                {displayedItems.length == 0 ? (
                                    <Typography.Text type='secondary'>No deployment units available.</Typography.Text>
                                ) : (
                                    displayedItems.map((unit) => (
                                        <IconTextButton
                                            key={unit.uuid}
                                            type='link'
                                            label={unit.label}
                                            onClick={unit.onView}
                                        />
                                    ))
                                )}
                            </Skeleton>
                        ),
                    },
                ]}
            />
        </React.Fragment>
    );
};

const DeploymentDetailsCollapse = (props: IDeploymentDetailsCollapse): JSX.Element => {
    const { selectedDeployment } = props;
    const deploymentStore = useDeploymentStore();
    const accountStore = useAccountStore();
    const deviceGroupStore = useDeviceGroupsStore();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [deploymentDetails, setDeploymentDetails] = React.useState<DeploymentDetails>(() => new DeploymentDetails());
    const [deviceGroupDetails, setDeviceGroupDetails] = React.useState<IDeploymentDetailsDeviceGroupRecord[]>([]);
    const [expandedKeys, setExpandedKeys] = React.useState<string[]>(
        selectedDeployment ? [String(selectedDeployment.id)] : [],
    );
    const apiAccountId = React.useMemo(
        () => accountStore.getAccountByUuid(selectedDeployment.accountUuid)?.id,
        [selectedDeployment],
    );

    const [reFetchAfterTimeoutCount, setReFetchAfterTimeoutCount] = React.useState(1);

    const fetchData = async (): Promise<void> => {
        try {
            setLoading(true);
            if (!apiAccountId) {
                setLoading(false);
                return;
            }

            const loadedDeploymentDetails = await deploymentStore.getDeploymentDetails(
                apiAccountId,
                selectedDeployment.id,
                { includeDeviceConnectivity: true },
            );
            setDeploymentDetails(loadedDeploymentDetails);
            const { deviceGroups: loadedDeviceGroupDetails, devicesWithoutConnectivityCount } =
                DeploymentListingHelpers.buildDeviceGroupDetails(loadedDeploymentDetails.targets);

            if (devicesWithoutConnectivityCount > 0) {
                if (reFetchAfterTimeoutCount <= MAX_TIMES_TO_RE_FETCH_DATA) {
                    notification.info({
                        message: `${getMovingToDeviceGroupMessage(devicesWithoutConnectivityCount)} Waiting...`,
                        placement: 'topRight',
                        duration: TIME_BEFORE_RE_FETCH_IN_SECONDS,
                    });
                    setTimeout(() => {
                        setReFetchAfterTimeoutCount((prev) => ++prev);
                    }, TIME_BEFORE_RE_FETCH_IN_SECONDS * 1000);
                } else {
                    notification.error({
                        message: `${getMovingToDeviceGroupMessage(devicesWithoutConnectivityCount)} Please try the operation again later`,
                        placement: 'topRight',
                    });
                    setLoading(false);
                }
            } else {
                setDeviceGroupDetails(loadedDeviceGroupDetails);
                setLoading(false);
            }
        } catch (error) {
            notification.error({
                message: getErrorMessage(error),
                placement: 'topRight',
            });
            setLoading(false);
        }
    };

    React.useEffect(() => {
        (async () => {
            try {
                await fetchData();
            } catch (error) {
                notification.error({
                    message: getErrorMessage(error),
                    placement: 'topRight',
                });
            }
        })();
    }, [selectedDeployment, reFetchAfterTimeoutCount]);

    const DeviceGroupCollapse = () => {
        return (
            <BasicCollapse
                defaultActiveKey={['deviceGroupPanelHeader']}
                panels={[
                    {
                        header: <span className={deploymentCss.collapseHeader}>Device groups</span>,
                        key: 'deviceGroupPanelHeader',
                        content: (
                            <BasicCollapse
                                className={deploymentCss.deviceGroupCollapseContain}
                                defaultActiveKey={expandedKeys}
                                onChange={(key) => {
                                    setExpandedKeys([...key]);
                                }}
                                panels={deviceGroupDetails.map((groupDetail) => {
                                    const { online: onlineDevicesList, offline: offlineDevicesList } =
                                        DeploymentListingHelpers.partitionDevicesByConnectivity(groupDetail.devices);
                                    const DeviceGroupHeader = () => {
                                        return (
                                            <div className={deploymentCss.collapseHeader}>
                                                <Icon iconName='object-ungroup' size='SMALL' />
                                                <div className={deploymentCss.text}>
                                                    <span>{groupDetail.label}</span>
                                                    {groupDetail.devices.length ? (
                                                        <Label
                                                            className={deploymentCss.subText}
                                                            label={
                                                                '(Deployed to ' +
                                                                groupDetail.numberOfDeployedDevices +
                                                                '/' +
                                                                groupDetail.devices.length +
                                                                ' Devices)'
                                                            }
                                                        />
                                                    ) : null}
                                                </div>
                                            </div>
                                        );
                                    };

                                    const DeploymentStatusComponent = (
                                        deviceGroup: IDeploymentDetailsDeviceGroupRecord,
                                    ): JSX.Element => {
                                        const timezone = getTimezones(
                                            [deviceGroup.name],
                                            (deviceGroupName: string) =>
                                                deviceGroupStore.getDeviceGroupByName(deviceGroupName)
                                                    ?.deviceGroupTimezone,
                                        );
                                        const displayTimezone = isNullOrEmpty(timezone) ? '' : `(${timezone})`;
                                        const stepItems: IStepItem[] = [];
                                        if (deviceGroup.downloadOnly) {
                                            const scheduleDateTime = deviceGroup.scheduleDateTime;
                                            const isFinish = dateCompare(Date.now(), scheduleDateTime);
                                            stepItems.push({
                                                code: '1',
                                                label: (
                                                    <strong className={deploymentCss.stepHeadder}>
                                                        Downloaded packages
                                                    </strong>
                                                ),
                                                description: (
                                                    <div>
                                                        Download date: {formatDate(scheduleDateTime)} {displayTimezone}
                                                    </div>
                                                ),
                                                status: isFinish ? 'finish' : 'wait',
                                                className: isFinish ? deploymentCss.stepsItem : undefined,
                                            });
                                        }

                                        const installDateTime = deviceGroup.downloadOnly
                                            ? deviceGroup.installDateTime
                                            : deviceGroup.scheduleDateTime;
                                        const isFinish = dateCompare(Date.now(), installDateTime);
                                        stepItems.push({
                                            code: deviceGroup.downloadOnly ? '2' : '1',
                                            label: (
                                                <strong className={deploymentCss.stepHeadder}>Install packages</strong>
                                            ),
                                            description: (
                                                <div>
                                                    Installation date: {formatDate(installDateTime)} {displayTimezone}
                                                </div>
                                            ),
                                            status: isFinish ? 'finish' : 'wait',
                                            className: isFinish ? deploymentCss.stepsItem : undefined,
                                        });

                                        return isNullOrEmpty(stepItems) ? null : <VerticalStepper items={stepItems} />;
                                    };

                                    return {
                                        key: groupDetail.name,
                                        header: <DeviceGroupHeader />,
                                        content: (
                                            <div>
                                                <DeploymentStatusComponent {...groupDetail} />
                                                <RowLayout
                                                    childItems={[
                                                        <DeviceProgressTable
                                                            title='Online'
                                                            accountId={apiAccountId}
                                                            dataSource={onlineDevicesList}
                                                        />,
                                                        <DeviceProgressTable
                                                            title='Offline'
                                                            accountId={apiAccountId}
                                                            dataSource={offlineDevicesList}
                                                        />,
                                                    ]}
                                                    childCols={[12, 12]}
                                                    gutter={12}
                                                />
                                            </div>
                                        ),
                                    };
                                })}
                            />
                        ),
                        extra: (
                            <IconButton
                                key='reloadItems'
                                className={deploymentCss['reload-device-group-btn']}
                                iconName='arrow-rotate-right'
                                onClick={fetchData}
                            />
                        ),
                    },
                ]}
            />
        );
    };

    return (
        <Skeleton loading={loading} noOfRows={1}>
            <DeploymentUnitCollapse accountId={apiAccountId} units={deploymentDetails.units} />
            <DeviceGroupCollapse />
        </Skeleton>
    );
};

const DeviceProgressTable = (props: {
    title: string;
    accountId: number;
    dataSource: IDeploymentDetailsDeviceRecord[];
}): JSX.Element => {
    const { title, accountId, dataSource } = props;
    return (
        <BasicTable
            className={deploymentCss.emptyComponent}
            columns={[
                {
                    code: 'deviceName',
                    title,
                    dataIndex: 'deviceName',
                    width: '60%',
                    render: (uid: string) => <DeviceUuidHyperlink uid={uid} accountId={accountId} />,
                    sorter: (a: IDeploymentDetailsDeviceRecord, b: IDeploymentDetailsDeviceRecord) =>
                        a.deviceName < b.deviceName ? 1 : a.deviceName > b.deviceName ? -1 : 0,
                    textSearchPlaceholder: 'Search',
                    onFilter: (title: string, device: IDeploymentDetailsDeviceRecord) =>
                        device.deviceName?.toLowerCase().includes(title.toLowerCase()),
                    iconName: 'search',
                },
                {
                    code: 'progress',
                    title: 'Progress status',
                    dataIndex: 'progress',
                    render: (progress: IDeploymentDetailsDeviceRecord['progress']) => {
                        if (!progress) return null;
                        const status = progress.status;
                        const statusName = DeploymentListingHelpers.getProgressIconName(status);
                        const stage = progress.stage;
                        const progressBarPercent = DeploymentListingHelpers.getProgressBarPercent(stage);
                        return (
                            <div className={deploymentCss['progress-status-div']}>
                                {progressBarPercent && (
                                    <Progress
                                        type='line'
                                        percent={progressBarPercent}
                                        extraProps={{
                                            strokeColor: DeploymentListingHelpers.getProgressBarColor(status, stage),
                                            showInfo: progressBarPercent !== 100,
                                        }}
                                    />
                                )}
                                {statusName && (
                                    <Icon
                                        iconPrefix='fas'
                                        iconName={statusName}
                                        size='SMALL'
                                        className={cx(
                                            deploymentCss['progress-status-icon-div'],
                                            DeploymentListingHelpers.isErrorProgressStatus(status)
                                                ? deploymentCss['progress-error-status']
                                                : deploymentCss['progress-success-status'],
                                        )}
                                    />
                                )}
                            </div>
                        );
                    },
                    sorter: DeploymentListingHelpers.compareProgressStatus,
                    filters: [
                        { text: 'Queued/In-Progress', value: PROGRESS_STAGE.IN_PROGRESS },
                        { text: 'Succeeded', value: PROGRESS_STAGE.SUCCEEDED },
                        { text: 'Failed', value: PROGRESS_STAGE.FAILED },
                    ],
                    onFilter: DeploymentListingHelpers.matchProgressFilter,
                },
            ]}
            dataSource={dataSource.slice().sort(DeploymentListingHelpers.sortDevices)}
            rowKey='deviceName'
            extraOptions={{
                pagination: { hideOnSinglePage: true },
            }}
        />
    );
};
